import { Controller } from 'react-hook-form';
import { length, prop } from 'ramda';
import { Icon, InfoTooltip, Loading, Message, UploadFileInput } from '@fasstech/middle-design-system';
import useControlledFileUpload from './useControlledFileUpload';
import hasError from '../helpers/hasError';
import getErrorMessage from '../helpers/getErrorMessage';
import RemoveUploadFileModal from '../../displayer/RemoveUploadFileModal';
import { mapIndexed } from '@@serverHelpers/index.mjs';

const ControlledFileUpload = ({
  hideLabel = false,
  rules = {},
  control,
  name,
  label,
  filesType,
  formState: { errors },
  deleteMutation,
  getMiddleText,
  setError,
  clearErrors,
  uploadedFiles,
  maxFileSize,
  uploadedFileLimit,
  accept = '',
  projectId,
  informations
}) => {
  const { onChange, isLoading, fileToRemove, setFileToRemove, fields, onRemove } =
    useControlledFileUpload({
      control,
      name,
      getMiddleText,
      clearErrors,
      setError,
      deleteMutation,
      uploadedFiles,
      maxFileSize,
      uploadedFileLimit,
      projectId
    });

  const labelClasses = hideLabel
    ? 'hidden'
    : 'bg-secondary-lightest text-secondary font-semibold px-3 py-1 rounded-full';

  return (
    <div>
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={(props) => {
          return (
            <div className={'bg-gray-50 p-4 flex flex-col items-center rounded-lg'}>
              <div className={'flex justify-center items-center gap-2'}>
                <label className={labelClasses}>
                  {label} {prop('required', rules) && '*'}
                </label>
                {informations && <InfoTooltip text={informations} />}
              </div>
              <div className={'grid gap-y-2 mb-4'}>
                {mapIndexed((file, index) => {
                  if (file) {
                    const { fileName, uploadId } = file;

                    return (
                      <div
                        key={file.id}
                        className={
                          'bg-gray-100 text-dark-neutral-400 py-2 px-4 flex justify-between'
                        }>
                        <p>{fileName}</p>

                        <button
                          type="button"
                          onClick={() => setFileToRemove({ index, uploadId })}
                          data-testid="deleteFile">
                          <Icon name="delete" size={20} />
                        </button>
                      </div>
                    );
                  }
                }, fields)}
              </div>
              {fileToRemove && (
                <RemoveUploadFileModal
                  setDisplayModal={() => setFileToRemove(null)}
                  onRemove={onRemove}
                  isDisplayed={!!fileToRemove}
                  removeFileMessage={getMiddleText('REMOVE_FILE_MESSAGE')}
                  cancelRemoveFileLabel={getMiddleText('CANCEL_REMOVE_FILE_BUTTON')}
                  confirmRemoveFileLabel={getMiddleText('CONFIRM_REMOVE_FILE_BUTTON')}
                />
              )}

              <div
                className={`flex ${length(fields) === uploadedFileLimit ? 'pointer-events-none opacity-25' : ''}`}>
                <UploadFileInput
                  {...props}
                  {...props.field}
                  onChange={({ target }) =>
                    onChange({
                      target,
                      fileType: filesType
                    })
                  }
                  onClick={(event) => (event.target.value = '')}
                  iconSize={18}
                  accept={accept}
                  fileType={filesType}
                  multiple={true}
                  data-testid={`${filesType}UploadFile`}
                />
                {isLoading && <Loading size={'small'} />}
              </div>
              {hasError(errors, name) && (
                <Message
                  variant="error"
                  className="mt-2 text-sm"
                  label={getErrorMessage(errors, name)}
                />
              )}
            </div>
          );
        }}
      />
    </div>
  );
};

export default ControlledFileUpload;
